import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useUsageBilling } from '@contexts/UsageBillingContext';
import routes from '@config/routes';
import { StyledButton } from '@sharedRedesign/controls/Button/StyledButton';
import { StyledSvgIconWrap } from '@components/Managers/shared/components/StyledFlowEditor';
import { StyledFreeTierAccordionSummary } from '../StyledFreeTier';
import { StyledAccordion, StyledAccordionDetails, StyledFlex, StyledText } from '@shared/styles/styled';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { USAGE_STATUSES } from '@utils/constants';

const FreeTierUsageWarning = () => {
  const { currentUsages, sortedUsageMetrics } = useUsageBilling();
  const { colors, statusColors } = useTheme();
  const navigate = useNavigate();

  const showUsageWarning = Object.values(USAGE_STATUSES).includes(currentUsages?.status);

  const warningsConfig = {
    [USAGE_STATUSES.SAFE_LIMIT]: {
      icon: (
        <StyledSvgIconWrap themeFillColor="statusUnassigned" width="35px" height="35px">
          <InfoOutlinedIcon />
        </StyledSvgIconWrap>
      ),
      bodyColor: statusColors.mutedBlue.bg,
      headerText: 'Your Currently Limited To The Free Tier',
      bodyTextFirst: `You have not added any payment information yet, meaning you can only use transactions up until the free tier limit each month.`,
      bodyTextSecond: `Once you reach the free tier limits,
           you will face issues including Agents no longer working and Processes not executing. To prevent service disruptions, add a credit card to your account.`,
    },
    [USAGE_STATUSES.NEAR_LIMIT]: {
      icon: (
        <StyledSvgIconWrap themeFillColor="mustardBrown" width="32px" height="32px">
          <WarningAmberRoundedIcon />
        </StyledSvgIconWrap>
      ),
      labelColor: colors.mustardBrown,
      bodyColor: statusColors.burntYellow.bg,
      headerText: 'You Are Nearing The End of The Free Tier Limit',
      bodyTextFirst: `You are close to reaching your usage limit for`,
      bodyTextSecond: `transactions. None of the features associated with these transactions will work until your next monthly period,
       which can result in issues such as chat widgets that don't work, and broken Processes. Add a credit card to continue using Symphona uninterrupted.`,
    },
    [USAGE_STATUSES.OVER_LIMIT]: {
      icon: (
        <StyledSvgIconWrap themeFillColor="validationError" width="32px" height="32px">
          <WarningAmberRoundedIcon />
        </StyledSvgIconWrap>
      ),
      labelColor: colors.validationError,
      bodyColor: statusColors.venetianRed.bg,
      headerText: 'You Have Reached The Free Tier Limit',
      bodyTextFirst: `You have reached 100% usage for`,
      bodyTextSecond: `transactions. None of the features associated with these transactions will work until your next monthly period,\
       which can result in issues such as chat widgets that don't work, and broken Processes. Add a credit card to continue using Symphona uninterrupted`,
    },
  };

  const handleAddPaymentClick = () => {
    navigate({ pathname: routes.SETTINGS_BILLING_TAB, search: '?isPaymentModalOpen=true' });
  };

  const renderAddPaymentButton = () => (
    <StyledFlex minWidth="200px" justifyContent="center">
      <StyledButton primary variant="contained" onClick={handleAddPaymentClick}>
        Add Credit Card
      </StyledButton>
    </StyledFlex>
  );

  const renderImpactedUsages = () =>
    sortedUsageMetrics()?.map((metric, index) => {
      const unitType = metric?.totalUsageUnits;
      const metricUsage = metric?.totalUsage || 0;
      const totalUsage = metricUsage % 1 !== 0 ? metricUsage.toFixed(2) : metricUsage;

      return (
        <StyledFlex direction="row" key={index}>
          <StyledFlex width="17px">
            <StyledText weight={600}>{index + 1}.</StyledText>
          </StyledFlex>

          <StyledFlex>
            <StyledText>
              <StyledText as="span" display="inline" weight={600} capitalize>
                {metric.itemName}:{' '}
              </StyledText>
              {totalUsage}/{metric.maxLimitUsage} ({metric.usagePercentage}%) {unitType} used
            </StyledText>
          </StyledFlex>
        </StyledFlex>
      );
    });

  const renderSafeUsageWarning = () => {
    const currentBannerConfig = warningsConfig[currentUsages.status];

    const isLimitUsageUnsafe = USAGE_STATUSES.SAFE_LIMIT !== currentUsages?.status;

    return (
      <StyledFlex backgroundColor={currentBannerConfig.bodyColor} padding="20px" direction="row" gap="25px">
        <StyledFlex direction="row" gap="20px">
          <StyledFlex justifyContent="center">{currentBannerConfig.icon}</StyledFlex>

          <StyledFlex>
            <StyledFlex>
              <StyledText weight={600}>
                {isLimitUsageUnsafe && (
                  <StyledText as="span" display="inline" weight={700} color={currentBannerConfig.labelColor}>
                    Warning:{' '}
                  </StyledText>
                )}

                {currentBannerConfig.headerText}
              </StyledText>

              <StyledText weight={400} mt={5}>
                {currentBannerConfig.bodyTextFirst}{' '}
                {isLimitUsageUnsafe && (
                  <StyledText as="span" display="inline" weight={700}>
                    {currentUsages?.limitCount || 0}
                  </StyledText>
                )}{' '}
                {currentBannerConfig.bodyTextSecond}
              </StyledText>
            </StyledFlex>

            {isLimitUsageUnsafe && (
              <StyledFlex marginLeft="-8px">
                <StyledAccordion>
                  <StyledFreeTierAccordionSummary
                    p="0 "
                    borderRadius="8px"
                    expandIcon={<ExpandMoreRounded />}
                    flexDirection="row-reverse"
                    iconColor={colors.primary}
                    hoverBg="transparent"
                    style={{ width: 'fit-content' }}
                  >
                    <StyledText weight={500}>View Impacted Usages</StyledText>
                  </StyledFreeTierAccordionSummary>

                  <StyledAccordionDetails>
                    <StyledFlex marginLeft="10px">{renderImpactedUsages()}</StyledFlex>
                  </StyledAccordionDetails>
                </StyledAccordion>
              </StyledFlex>
            )}
          </StyledFlex>
        </StyledFlex>

        {renderAddPaymentButton()}
      </StyledFlex>
    );
  };

  return showUsageWarning && <StyledFlex>{renderSafeUsageWarning()}</StyledFlex>;
};

export default FreeTierUsageWarning;
